// Thin REST client for the Helinhair webshop API's public endpoints
// (`/api/webshop-public/...`). No auth - every endpoint the storefront calls is public.

import type {
  Paged,
  Product,
  Category,
  Brand,
  InventoryItem,
  ShippingMethod,
  PickupPoint,
  Order,
  CreateOrderRequest,
  CreateCheckoutSessionRequest,
  CheckoutSessionResponse,
  CheckoutSettings,
} from './types'
import type { DiscountValidation, DiscountValidationRequest } from './discountCode'

const DEFAULT_BASE_URL = 'https://api.dev.helinhair.dk'

/**
 * Resolves the API origin from `VITE_SHOP_API_URL`, falling back to the dev gateway.
 * The fallback is for local-dev convenience; deployed builds should set the env var
 * explicitly so a missing one is loud in DevTools, not a silent wrong-environment read.
 */
export function webshopBaseUrl(): string {
  const raw = (import.meta.env.VITE_SHOP_API_URL as string | undefined)?.trim()
  if (raw && raw.length > 0) return raw.replace(/\/+$/, '')
  console.warn(
    '[shop] VITE_SHOP_API_URL not set - using the dev gateway as fallback. ' +
      'Set it explicitly in your env to avoid silently reading the wrong environment.',
  )
  return DEFAULT_BASE_URL
}

/** Serialises a params object into a query string, dropping empty values. */
export function buildQuery(params?: Record<string, unknown>): string {
  if (!params) return ''
  const sp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue
    const str = String(value)
    if (str.length === 0) continue
    sp.set(key, str)
  }
  const qs = sp.toString()
  return qs ? `?${qs}` : ''
}

interface Problem {
  title?: string
  detail?: string
  status?: number
}

export class ApiError extends Error {
  status: number
  problem: unknown
  constructor(message: string, status: number, problem: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST'
  params?: Record<string, unknown>
  body?: unknown
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', params, body } = opts
  const init: RequestInit = { method, headers: { Accept: 'application/json' } }
  if (body !== undefined) {
    init.headers = { ...init.headers, 'Content-Type': 'application/json' }
    init.body = JSON.stringify(body)
  }
  const res = await fetch(`${webshopBaseUrl()}${path}${buildQuery(params)}`, init)
  const text = await res.text()
  const data = text ? JSON.parse(text) : null
  if (!res.ok) {
    const problem = (data ?? {}) as Problem
    throw new ApiError(
      problem.detail || problem.title || `Shop API request failed: ${res.status}`,
      res.status,
      problem,
    )
  }
  return data as T
}

/**
 * Drains the API's cursor pagination into a single array. Guards against a
 * misbehaving cache/cursor that never advances (same/empty/already-seen) so a
 * stuck loop can never hang the UI or repeat a page indefinitely. When
 * `keyOf` is given, items are also deduped by that key (first-seen wins) -
 * needed because a stuck cursor re-fetches its last page before the loop
 * notices and stops, which would otherwise re-append it.
 */
export async function fetchAllPages<T>(
  path: string,
  params?: Record<string, unknown>,
  maxPages = 50,
  keyOf?: (item: T) => string,
): Promise<T[]> {
  const seenKeys = keyOf ? new Set<string>() : null
  const items: T[] = []
  const seenCursors = new Set<string>()
  let cursor: string | undefined
  for (let page = 0; page < maxPages; page++) {
    const res = await request<Paged<T>>(path, { params: { ...params, limit: 100, cursor } })
    for (const item of res.items) {
      if (seenKeys) {
        const key = keyOf!(item)
        if (seenKeys.has(key)) continue
        seenKeys.add(key)
      }
      items.push(item)
    }
    const next = res.nextCursor ?? undefined
    if (!next || next === cursor || seenCursors.has(next)) break
    seenCursors.add(next)
    cursor = next
  }
  return items
}

export function listProducts(): Promise<Product[]> {
  return fetchAllPages<Product>('/api/webshop-public/products', undefined, 50, (p) => p.id)
}

export function getProduct(id: string): Promise<Product> {
  return request<Product>(`/api/webshop-public/products/${encodeURIComponent(id)}`)
}

export function listCategories(): Promise<Category[]> {
  return fetchAllPages<Category>('/api/webshop-public/categories', undefined, 50, (c) => c.id)
}

export function listBrands(): Promise<Brand[]> {
  return fetchAllPages<Brand>('/api/webshop-public/brands', undefined, 50, (b) => b.id)
}

export function listInventory(): Promise<InventoryItem[]> {
  return fetchAllPages<InventoryItem>('/api/webshop-public/inventory', undefined, 50, (i) => i.sku)
}

/**
 * The methods a customer can choose. `lang` gives names and delivery times in the site language
 * (BUG-421); `country` keeps the methods that deliver to the shipping address's country (BUG-422).
 */
export function listShippingMethods(options: { lang?: string; country?: string } = {}): Promise<ShippingMethod[]> {
  const params = Object.fromEntries(Object.entries(options).filter(([, v]) => v)) as Record<string, string>
  return fetchAllPages<ShippingMethod>('/api/webshop-public/shipping-methods', Object.keys(params).length ? params : undefined, 50, (m) => m.id)
}

/**
 * Pickup points near the given address for a `requiresPickupPoint` shipping method, resolved by
 * the backend from that method's carrier integration (e.g. Shipmondo) - never a carrier API
 * called directly from the browser.
 */
export function listPickupPoints(
  methodId: string,
  address: { zipcode?: string; city?: string; address?: string; country?: string; lat?: number; lng?: number },
): Promise<PickupPoint[]> {
  return request<PickupPoint[]>(`/api/webshop-public/shipping-methods/${encodeURIComponent(methodId)}/pickup-points`, {
    params: address,
  })
}

export function createOrder(body: CreateOrderRequest): Promise<Order> {
  return request<Order>('/api/webshop-public/orders', { method: 'POST', body })
}

/**
 * BUG-518: whether a discount code applies to these basket lines, and what it takes off the goods.
 * A code that does not apply is a 400 with a `DISCOUNT_CODE_*` code (read it with discountRefusalOf).
 */
export function validateDiscountCode(body: DiscountValidationRequest): Promise<DiscountValidation> {
  return request<DiscountValidation>('/api/webshop-public/discount-codes/validation', { method: 'POST', body })
}

export function getOrder(id: string): Promise<Order> {
  return request<Order>(`/api/webshop-public/orders/${encodeURIComponent(id)}`)
}

/**
 * The shop's payment presentation (admin setting, public read). Embedded is the default and the
 * fallback when the setting is unreadable (owner 2026-10-19: customers pay in the shop); only a saved
 * Redirect choice sends them to Stripe's page.
 */
export async function getCheckoutSettings(): Promise<CheckoutSettings> {
  try {
    const s = await request<CheckoutSettings>('/api/webshop-public/settings/checkout')
    return s?.presentation === 'REDIRECT' || s?.presentation === 'EMBEDDED' || s?.presentation === 'CUSTOM'
      ? s
      : { presentation: 'EMBEDDED' }
  } catch {
    return { presentation: 'EMBEDDED' }
  }
}

export function createCheckoutSession(
  orderId: string,
  body: CreateCheckoutSessionRequest,
): Promise<CheckoutSessionResponse> {
  return request<CheckoutSessionResponse>(
    `/api/webshop-public/orders/${encodeURIComponent(orderId)}/checkout-session`,
    { method: 'POST', body },
  )
}

export function cancelOrder(id: string, reason: string): Promise<Order> {
  return request<Order>(`/api/webshop-public/orders/${encodeURIComponent(id)}/cancellation`, {
    method: 'POST',
    body: { reason },
  })
}
